import { create } from 'zustand';
import { posStore } from './posStore.js';

/**
 * Live "spor etter båt" — a rolling breadcrumb of recent GPS fixes drawn behind
 * own-ship (distinct from recorded Turer). Capped by point count so the canvas
 * polyline stays cheap on old GPUs (spec 02). Points are in-memory only; the
 * mode is persisted.
 */
export type TrailMode = 'off' | 'short' | 'medium' | 'long';

const KEY = 'los.trail';

/** Max positions kept per mode. At ~1 fix/s: 500 ≈ 8 min, 2000 ≈ 33 min, 8000 ≈ 2 h. */
export const TRAIL_CAP: Record<TrailMode, number> = {
  off: 0,
  short: 500,
  medium: 2000,
  long: 8000,
};

interface LatLon {
  lat: number;
  lon: number;
}

function loadMode(): TrailMode {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'off' || v === 'short' || v === 'medium' || v === 'long') return v;
  } catch {
    /* private mode */
  }
  return 'medium';
}

interface TrailState {
  mode: TrailMode;
  points: LatLon[];
  setMode: (mode: TrailMode) => void;
  /** Append a fix, trimming to the active cap. No-op when off. */
  push: (lat: number, lon: number) => void;
}

export const useTrailStore = create<TrailState>((set, get) => ({
  mode: loadMode(),
  points: [],

  setMode: (mode) => {
    try {
      localStorage.setItem(KEY, mode);
    } catch {
      /* ignore */
    }
    // Off clears the buffer (frees memory, restarts the trail on re-enable);
    // a shorter cap trims the tail.
    set({ mode, points: mode === 'off' ? [] : get().points.slice(-TRAIL_CAP[mode]) });
  },

  push: (lat, lon) => {
    const { mode, points } = get();
    if (mode === 'off') return;
    const cap = TRAIL_CAP[mode];
    const next = points.length >= cap ? [...points.slice(points.length - cap + 1), { lat, lon }] : [...points, { lat, lon }];
    set({ points: next });
  },
}));

/** Feed fixes into the trail app-wide, so it's already there on the map screen. */
export function initTrail(): void {
  posStore.subscribe((s, prev) => {
    if (s.fix !== prev.fix && s.fix) useTrailStore.getState().push(s.fix.lat, s.fix.lon);
  });
}
